<script setup>
import { computed, useSlots } from 'vue';
import { tv } from 'tailwind-variants';
import { CsIcon } from 'dashboard/components/ui/commslayer';

/**
 * @typedef {import('../icon/icon-types').IconName} IconName
 */

/**
 * @typedef {object} EmptyStateProps
 * @property {string} heading - The main heading text for the empty state
 * @property {string} content - The descriptive content text for the empty state
 * @property {('no-data'|'no-result-found'|'')} [iconType=''] - Type of icon to display ('no-data', 'no-result-found', or empty for none)
 * @property {('default'|'sm')} [size='default'] - Size variant of the empty state
 * @property {IconName} [iconName=''] - Name of the icon to display
 */

/**
 * @type {EmptyStateProps}
 */
const props = defineProps({
  heading: {
    type: String,
    required: true,
  },
  content: {
    type: String,
    required: true,
  },
  iconType: {
    type: String,
    required: false,
    default: '',
  },
  size: {
    type: String,
    required: false,
    default: 'default',
  },
  iconName: {
    type: String,
    required: false,
    default: '',
  },
});

const emptyStateRecipe = tv({
  slots: {
    root: 'flex flex-col gap-4 items-center justify-center',
    icon: 'text-brand-gray-14',
    heading: 'font-semibold text-brand-text max-w-sm text-center !mb-0',
    content:
      'text-sm text-brand-gray-14 dark:text-brand-gray-12 max-w-sm text-center',
  },
  variants: {
    size: {
      xs: {
        root: 'gap-1',
        icon: 'w-8 h-8',
        heading: '!text-sm',
        content: 'text-xs',
      },
      sm: {
        root: 'gap-1',
        icon: 'w-10 h-10',
        heading: '!text-sm',
        content: 'text-xs',
      },
      default: {
        root: 'gap-4',
        icon: 'w-16 h-16',
        heading: '!text-base',
        content: 'text-sm',
      },
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const slots = useSlots();
function hasSlot(name) {
  return !!slots[name];
}

const emptyStateClasses = computed(() =>
  emptyStateRecipe({
    size: props.size,
  })
);
</script>

<template>
  <div
    data-component="CsEmptyState"
    data-slot="root"
    :class="emptyStateClasses.root()"
  >
    <CsIcon
      v-if="props.iconName"
      data-slot="icon"
      :name="props.iconName"
      :class="emptyStateClasses.icon()"
    />
    <CsIcon
      v-else-if="props.iconType === 'no-data'"
      data-slot="icon"
      name="folder"
      :class="emptyStateClasses.icon()"
    />
    <CsIcon
      v-else-if="props.iconType === 'no-result-found'"
      data-slot="icon"
      name="search-resource"
      :class="emptyStateClasses.icon()"
    />

    <div class="flex flex-col gap-2 items-center justify-center">
      <h1 data-slot="heading" :class="emptyStateClasses.heading()">
        {{ props.heading }}
      </h1>

      <span
        v-if="hasSlot('content')"
        data-slot="content"
        :class="emptyStateClasses.content()"
      >
        <slot name="content" />
      </span>
      <span v-else data-slot="content" :class="emptyStateClasses.content()">
        {{ props.content }}
      </span>

      <slot />
    </div>
  </div>
</template>
